import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DollarOutlined,
  EyeOutlined,
  ReloadOutlined,
  SearchOutlined,
  WalletOutlined,
} from '@ant-design/icons';
import { API_ENDPOINTS, buildApiUrl } from '../../api/config';
import { get, post } from '../../api/request';
import './RefundManagementPage.css';

type RefundStatus = 'pending' | 'approved' | 'rejected';

interface RefundItem {
  id: string;
  user: string;
  product: string;
  channel: string;
  amount: number;
  ink: number;
  status: string;
  paidAt: string;
  source: string;
  refundStatus: RefundStatus;
  refundReason: string;
  refundRequestedAt?: string;
  refundProcessedAt?: string;
  refundRemark?: string;
  refundOperator?: string;
  frozenInk: number;
}

interface RefundResponse {
  data: RefundItem[];
  total: number;
  summary: {
    pendingCount: number;
    pendingAmount: number;
    approvedAmount: number;
    rejectedCount: number;
  };
}

const emptySummary: RefundResponse['summary'] = {
  pendingCount: 0,
  pendingAmount: 0,
  approvedAmount: 0,
  rejectedCount: 0,
};

const statusMeta: Record<RefundStatus, { color: string; text: string }> = {
  pending: { color: 'processing', text: '待审核' },
  approved: { color: 'success', text: '已退款' },
  rejected: { color: 'error', text: '已驳回' },
};

const displayTime = (value?: string) => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString('zh-CN', { hour12: false });
};

const RefundManagementPage: React.FC = () => {
  const [keyword, setKeyword] = useState('');
  const [refundStatus, setRefundStatus] = useState<RefundStatus | 'all'>('pending');
  const [items, setItems] = useState<RefundItem[]>([]);
  const [summary, setSummary] = useState(emptySummary);
  const [loading, setLoading] = useState(false);
  const [reviewItem, setReviewItem] = useState<RefundItem | null>(null);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [remark, setRemark] = useState('');
  const [reviewing, setReviewing] = useState(false);

  const loadRefunds = useCallback(async () => {
    setLoading(true);
    try {
      const result = await get<RefundResponse>(buildApiUrl(API_ENDPOINTS.operations.refunds, {
        keyword: keyword.trim(),
        refund_status: refundStatus,
      }));
      setItems(result.data || []);
      setSummary(result.summary || emptySummary);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载退款申请失败');
    } finally {
      setLoading(false);
    }
  }, [keyword, refundStatus]);

  useEffect(() => {
    void loadRefunds();
  }, [loadRefunds]);

  const openReview = (record: RefundItem, action: 'approve' | 'reject') => {
    setReviewItem(record);
    setReviewAction(action);
    setRemark(action === 'approve' ? '退款资料核验通过' : '');
  };

  const submitReview = async () => {
    if (!reviewItem || !remark.trim()) {
      message.warning('请填写审核说明');
      return;
    }
    setReviewing(true);
    try {
      await post(API_ENDPOINTS.operations.reviewRefund(reviewItem.id), {
        action: reviewAction,
        remark: remark.trim(),
      });
      message.success(reviewAction === 'approve' ? '退款审核已通过' : '退款申请已驳回');
      setReviewItem(null);
      setRemark('');
      await loadRefunds();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '退款审核失败');
    } finally {
      setReviewing(false);
    }
  };

  const showDetail = (record: RefundItem) => {
    Modal.info({
      width: 620,
      title: `退款申请 · ${record.id}`,
      content: (
        <Descriptions column={1} size="small" className="refund-detail-list">
          <Descriptions.Item label="用户">{record.user}</Descriptions.Item>
          <Descriptions.Item label="商品">{record.product}</Descriptions.Item>
          <Descriptions.Item label="退款金额">¥{Number(record.amount || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="冻结墨水">{Number(record.frozenInk || record.ink || 0).toLocaleString()} 点</Descriptions.Item>
          <Descriptions.Item label="退款原因">{record.refundReason || '-'}</Descriptions.Item>
          <Descriptions.Item label="申请时间">{displayTime(record.refundRequestedAt)}</Descriptions.Item>
          <Descriptions.Item label="处理人">{record.refundOperator || '-'}</Descriptions.Item>
          <Descriptions.Item label="处理说明">{record.refundRemark || '-'}</Descriptions.Item>
        </Descriptions>
      ),
    });
  };

  const columns: ColumnsType<RefundItem> = [
    {
      title: '退款单',
      dataIndex: 'id',
      width: 190,
      fixed: 'left',
      render: (value: string) => <span className="refund-code">{value}</span>,
    },
    { title: '用户', dataIndex: 'user', width: 120 },
    {
      title: '订单内容',
      width: 190,
      render: (_, record) => (
        <div className="refund-product-cell">
          <strong>{record.product}</strong>
          <span>{record.channel} · {record.paidAt}</span>
        </div>
      ),
    },
    {
      title: '退款金额',
      dataIndex: 'amount',
      width: 110,
      render: (value: number) => <strong>¥{Number(value || 0).toFixed(2)}</strong>,
    },
    {
      title: '冻结墨水',
      width: 110,
      render: (_, record) => `${Number(record.frozenInk || record.ink || 0).toLocaleString()} 点`,
    },
    {
      title: '退款原因',
      dataIndex: 'refundReason',
      width: 220,
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'refundStatus',
      width: 100,
      render: (value: RefundStatus) => {
        const meta = statusMeta[value] || { color: 'default', text: value || '未知' };
        return <Tag color={meta.color}>{meta.text}</Tag>;
      },
    },
    {
      title: '申请时间',
      dataIndex: 'refundRequestedAt',
      width: 170,
      render: displayTime,
    },
    {
      title: '操作',
      width: 210,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EyeOutlined />} onClick={() => showDetail(record)}>详情</Button>
          {record.refundStatus === 'pending' && (
            <>
              <Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => openReview(record, 'approve')}>通过</Button>
              <Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => openReview(record, 'reject')}>驳回</Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="refund-management-page">
      <div className="refund-page-header">
        <div>
          <h1>退款审核</h1>
          <p>处理用户订单退款申请。通过后扣回冻结墨水，驳回后自动解冻并恢复可用余额。</p>
        </div>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={loadRefunds}>刷新</Button>
      </div>

      <Row gutter={[16, 16]} className="refund-summary-row">
        <Col xs={24} sm={12} xl={6}><Card bordered={false}><Statistic title="待审核申请" value={summary.pendingCount} suffix="笔" prefix={<WalletOutlined />} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card bordered={false}><Statistic title="待审核金额" value={summary.pendingAmount} precision={2} prefix="¥" /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card bordered={false}><Statistic title="累计已退款" value={summary.approvedAmount} precision={2} prefix={<DollarOutlined />} /></Card></Col>
        <Col xs={24} sm={12} xl={6}><Card bordered={false}><Statistic title="累计驳回" value={summary.rejectedCount} suffix="笔" prefix={<CloseCircleOutlined />} /></Card></Col>
      </Row>

      <Card bordered={false} className="refund-filter-card">
        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            value={keyword}
            placeholder="订单号 / 用户 / 退款原因"
            style={{ width: 280 }}
            onChange={(event) => setKeyword(event.target.value)}
            onPressEnter={loadRefunds}
          />
          <Select
            value={refundStatus}
            style={{ width: 150 }}
            onChange={setRefundStatus}
            options={[
              { label: '全部状态', value: 'all' },
              { label: '待审核', value: 'pending' },
              { label: '已退款', value: 'approved' },
              { label: '已驳回', value: 'rejected' },
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} loading={loading} onClick={loadRefunds}>查询</Button>
        </Space>
      </Card>

      <Card bordered={false} className="refund-table-card">
        <Table
          rowKey="id"
          columns={columns}
          dataSource={items}
          loading={loading}
          scroll={{ x: 1420 }}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </Card>

      <Modal
        title={reviewAction === 'approve' ? '通过退款申请' : '驳回退款申请'}
        open={Boolean(reviewItem)}
        okText={reviewAction === 'approve' ? '确认通过' : '确认驳回'}
        cancelText="取消"
        okButtonProps={{ danger: reviewAction === 'reject', disabled: !remark.trim(), loading: reviewing }}
        onOk={submitReview}
        onCancel={() => {
          if (reviewing) return;
          setReviewItem(null);
          setRemark('');
        }}
      >
        {reviewItem && (
          <div className="refund-review-modal">
            <div className="refund-review-summary">
              <span>{reviewItem.user} · {reviewItem.product}</span>
              <strong>¥{Number(reviewItem.amount || 0).toFixed(2)}</strong>
              <p>冻结 {Number(reviewItem.frozenInk || reviewItem.ink || 0).toLocaleString()} 墨水</p>
            </div>
            <p className="refund-review-reason">用户原因：{reviewItem.refundReason}</p>
            <label>
              审核说明
              <Input.TextArea
                rows={4}
                maxLength={200}
                showCount
                value={remark}
                placeholder={reviewAction === 'approve' ? '填写退款核验说明' : '填写驳回原因，用户端会展示'}
                onChange={(event) => setRemark(event.target.value)}
              />
            </label>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default RefundManagementPage;
